/**
 * ReassignModal — Supervisor-only reassignment.
 *
 * Emits `apiClient.retail.tasks.reassign({newAssigneeEmail, reason})`.
 */
import { useState } from 'react';
import { Banner, Button, ModalShell } from '@/components/primitives';
import type { TaskRecordDto } from '@retail-ops/shared-types';

export interface ReassignModalProps {
  task: TaskRecordDto;
  assigneeOptions: ReadonlyArray<{ email: string; label: string }>;
  onClose: () => void;
  onSubmit: (newAssigneeEmail: string, reason: string) => Promise<void> | void;
}

export function ReassignModal({
  task,
  assigneeOptions,
  onClose,
  onSubmit,
}: ReassignModalProps): JSX.Element {
  const [assignee, setAssignee] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = assignee.length > 0 && reason.trim().length >= 3;

  const handleSubmit = async (): Promise<void> => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(assignee, reason.trim());
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reassign failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      open
      onClose={onClose}
      title="Reassign task"
      ariaLabel={`Reassign task ${task.taskId}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleSubmit()}
            disabled={!canSubmit || submitting}
          >
            {submitting ? 'Reassigning…' : 'Reassign'}
          </Button>
        </>
      }
    >
      <p style={{ color: 'var(--ink-600)', fontSize: 13, margin: 0 }}>
        Currently assigned to <strong>{task.assignedTo}</strong>.
      </p>
      <div className="rtm-field">
        <label htmlFor="reassign-who">New assignee</label>
        <select
          id="reassign-who"
          value={assignee}
          onChange={(e) => setAssignee(e.target.value)}
        >
          <option value="">Select assignee…</option>
          {assigneeOptions.map((a) => (
            <option key={a.email} value={a.email}>
              {a.label} ({a.email})
            </option>
          ))}
        </select>
      </div>
      <div className="rtm-field">
        <label htmlFor="reassign-reason">Reason (required, ≥3 chars)</label>
        <textarea
          id="reassign-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="E.g. 'Out on leave — please cover.'"
        />
      </div>
      {error && <Banner tone="err">{error}</Banner>}
    </ModalShell>
  );
}
